import { Box, TableCell, TableRow } from '@mui/material';
import TouchRipple from '@mui/material/ButtonBase/TouchRipple';
import { alpha, styled, useTheme } from '@mui/material/styles';
import useTouchRipple from '@mui/material/useTouchRipple';
import { DateTime } from 'luxon';
import React, { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useGetAppointmentAccessibility } from 'src/features/visits/shared/hooks/useGetAppointmentAccessibility';
import { ExtendedMedicationDataForResponse, searchRouteByCode } from 'utils';
import { dataTestIds } from '../../../../../../constants/data-test-ids';
import { getEditOrderUrl, getInHouseMedicationDetailsUrl } from '../../../routing/helpers';
import { MedicationStatusChip } from '../statuses/MedicationStatusChip';
import { MedicationActions } from './MedicationActions';
import { MedicationBarcodeScan } from './MedicationBarcodeScan';
import { MedicationInteractionsAlertButton } from './MedicationInteractionsAlertButton';

interface MarTableRowProps {
  medication: ExtendedMedicationDataForResponse;
  columnStyles: Record<string, React.CSSProperties>;
}

const DATE_FORMAT = 'MM/dd/yyyy hh:mm a';

const StyledTouchRipple = styled(TouchRipple)(({ theme }) => ({
  '& .MuiTouchRipple-child': {
    backgroundColor: alpha(theme.palette.primary.main, 0.42),
  },
}));

export const MarTableRow: React.FC<MarTableRowProps> = ({ medication, columnStyles }) => {
  const navigate = useNavigate();
  const { id: appointmentId } = useParams();
  const rippleRef = React.useRef(null);
  const theme = useTheme();
  const { isAppointmentReadOnly: isReadOnly } = useGetAppointmentAccessibility();

  const { getRippleHandlers } = useTouchRipple({
    disabled: false,
    focusVisible: false,
    rippleRef,
  });

  const isPending = medication.status === 'pending';
  const isCompleted =
    medication.status === 'administered' ||
    medication.status === 'administered-partly' ||
    medication.status === 'administered-not';

  const handleRowClick = (): void => {
    if (isPending) {
      requestAnimationFrame(() => {
        navigate(`${getInHouseMedicationDetailsUrl(appointmentId!)}?scrollTo=${medication.id}`);
      });
    } else if (isCompleted) {
      requestAnimationFrame(() => {
        navigate(getEditOrderUrl(appointmentId!, medication.id));
      });
    }
  };

  const formatOrderDateTime = useMemo(() => {
    if (!medication.dateTimeCreated) return '-';

    const dt = DateTime.fromISO(medication.dateTimeCreated);

    if (!dt.isValid) return '-';

    return dt.toFormat(DATE_FORMAT);
  }, [medication.dateTimeCreated]);

  const formatGivenDateTime = useMemo(() => {
    // Try new format first: effectiveDateTime (ISO format)
    if (medication.effectiveDateTime) {
      const date = DateTime.fromISO(medication.effectiveDateTime);
      if (date.isValid) return date.toFormat(DATE_FORMAT);
    }

    // Fallback to deprecated format: dateGiven + timeGiven for backward compatibility
    if (medication.dateGiven && medication.timeGiven) {
      const date = DateTime.fromFormat(`${medication.dateGiven} ${medication.timeGiven}`, 'yyyy-MM-dd HH:mm:ss');
      if (date.isValid) return date.toFormat(DATE_FORMAT);
    }

    return '-';
  }, [medication.effectiveDateTime, medication.dateGiven, medication.timeGiven]);

  return (
    <TableRow
      data-testid={dataTestIds.inHouseMedicationsPage.marTable.medicationRow(medication.id)}
      sx={{
        cursor: isPending || isCompleted ? 'pointer' : 'default',
        position: 'relative',
        paddingLeft: '12px',
        ...(isPending || isCompleted
          ? {
              '&:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.04),
              },
              willChange: 'background-color',
            }
          : {}),
      }}
      onClick={handleRowClick}
      {...getRippleHandlers()}
    >
      <TableCell sx={columnStyles.interactionsAlert}>
        <MedicationInteractionsAlertButton medication={medication} />
      </TableCell>
      <TableCell data-testid={dataTestIds.inHouseMedicationsPage.marTable.medicationCell} sx={columnStyles.medication}>
        <MedicationBarcodeScan medication={medication} />
      </TableCell>
      <TableCell data-testid={dataTestIds.inHouseMedicationsPage.marTable.doseCell} sx={columnStyles.dose}>
        {medication.dose || ''} {medication.units || ''}
      </TableCell>
      <TableCell data-testid={dataTestIds.inHouseMedicationsPage.marTable.routeCell} sx={columnStyles.route}>
        {searchRouteByCode(medication.route)?.display || '-'}
      </TableCell>
      <TableCell sx={columnStyles.orderDateTime}>{formatOrderDateTime}</TableCell>
      <TableCell
        data-testid={dataTestIds.inHouseMedicationsPage.marTable.orderedByCell}
        sx={columnStyles.orderDateTime}
      >
        {medication.orderedByProvider || ''}
      </TableCell>
      {!isPending && (
        <>
          <TableCell sx={columnStyles.orderDateTime}>{formatGivenDateTime}</TableCell>
          <TableCell
            data-testid={dataTestIds.inHouseMedicationsPage.marTable.givenByCell}
            sx={columnStyles.orderDateTime}
          >
            {medication.administeredProvider || ''}
          </TableCell>
        </>
      )}
      <TableCell
        data-testid={dataTestIds.inHouseMedicationsPage.marTable.instructionsCell}
        sx={columnStyles.instructions}
      >
        {medication.instructions || ''}
      </TableCell>
      <TableCell data-testid={dataTestIds.inHouseMedicationsPage.marTable.statusCell} sx={columnStyles.status}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <MedicationStatusChip medication={medication} />
          {!isReadOnly && <MedicationActions medication={medication} />}
        </Box>
      </TableCell>
      <StyledTouchRipple ref={rippleRef} center={false} />
    </TableRow>
  );
};
